import React from 'react';
import { calculateCaloriesBurned } from '../utils/calories';
import { getDailyWeeklyMonthlyRanges } from '../utils/dateRanges';
import { toMillis } from '../utils/timestamps';

function computeStats(activities, currentWeight) {
  const { todayStart, weekStart, weekEnd, monthStart, monthEnd } = getDailyWeeklyMonthlyRanges();

  let dailyReps = 0, weeklyReps = 0, monthlyReps = 0;
  let dailyMinutes = 0, weeklyMinutes = 0, monthlyMinutes = 0;
  let dailyCalories = 0, weeklyCalories = 0, monthlyCalories = 0;

  activities.forEach(activity => {
    const activityDate = toMillis(activity.timestamp);
    const calories = calculateCaloriesBurned(activity, currentWeight);
    if (activityDate >= todayStart) {
      dailyReps += activity.count;
      dailyMinutes += activity.time;
      dailyCalories += calories;
    }
    if (activityDate >= weekStart && activityDate <= weekEnd) {
      weeklyReps += activity.count;
      weeklyMinutes += activity.time;
      weeklyCalories += calories;
    }
    if (activityDate >= monthStart && activityDate <= monthEnd) {
      monthlyReps += activity.count;
      monthlyMinutes += activity.time;
      monthlyCalories += calories;
    }
  });

  return {
    reps: { daily: dailyReps, weekly: weeklyReps, monthly: monthlyReps },
    minutes: { daily: dailyMinutes, weekly: weeklyMinutes, monthly: monthlyMinutes },
    calories: { daily: dailyCalories, weekly: weeklyCalories, monthly: monthlyCalories }
  };
}

// Resolves the three progress-bar denominators (daily/weekly/monthly) for a
// stat column. If the user has set a goal AND its type matches this column,
// the daily denominator is the goal target, and weekly/monthly scale as
// target*7 / target*30. Otherwise returns the provided fallback triple.
//
// Decisions:
// - goal.target is stored as a string (see Account handleGoalSave) so it's
//   parsed with parseInt. NaN / 0 / missing goal all fall back.
// - When a goal exists for one column, the other columns use the "reasonable
//   fallbacks" from the spec. When no goal exists at all, same fallbacks are
//   used for all three columns — a single unified default set instead of two.
//   Note: the calorie defaults (500/3500/15000) are lower than the previous
//   hardcoded values (2000/14000/60000) per spec.
function resolveDenoms(columnType, goal, fallback) {
  const target = goal && parseInt(goal.target);
  if (goal && goal.type === columnType && target && target > 0) {
    return { daily: target, weekly: target * 7, monthly: target * 30 };
  }
  return fallback;
}

function StatsWidget({ activities, currentWeight, goal }) {
  const stats = computeStats(activities, currentWeight);

  const repsDenoms     = resolveDenoms('reps',     goal, { daily: 1000, weekly: 7000,  monthly: 30000 });
  const minutesDenoms  = resolveDenoms('minutes',  goal, { daily: 60,   weekly: 420,   monthly: 1800  });
  const caloriesDenoms = resolveDenoms('calories', goal, { daily: 500,  weekly: 3500,  monthly: 15000 });

  return (
    <div className="user-stats">
      <h3>Your Stats</h3>
      <div className="stats-container">
        <div className="stats-column">
          <h4>Reps</h4>
          <div className="stat-item">
            <span>Daily Total: {stats.reps.daily}</span>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${Math.min(stats.reps.daily / repsDenoms.daily * 100, 100)}%` }} />
            </div>
          </div>
          <div className="stat-item">
            <span>Weekly Total: {stats.reps.weekly}</span>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${Math.min(stats.reps.weekly / repsDenoms.weekly * 100, 100)}%` }} />
            </div>
          </div>
          <div className="stat-item">
            <span>Monthly Total: {stats.reps.monthly}</span>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${Math.min(stats.reps.monthly / repsDenoms.monthly * 100, 100)}%` }} />
            </div>
          </div>
        </div>
        <div className="stats-column">
          <h4>Minutes</h4>
          <div className="stat-item">
            <span>Daily Total: {stats.minutes.daily}</span>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${Math.min(stats.minutes.daily / minutesDenoms.daily * 100, 100)}%` }} />
            </div>
          </div>
          <div className="stat-item">
            <span>Weekly Total: {stats.minutes.weekly}</span>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${Math.min(stats.minutes.weekly / minutesDenoms.weekly * 100, 100)}%` }} />
            </div>
          </div>
          <div className="stat-item">
            <span>Monthly Total: {stats.minutes.monthly}</span>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${Math.min(stats.minutes.monthly / minutesDenoms.monthly * 100, 100)}%` }} />
            </div>
          </div>
        </div>
        <div className="stats-column">
          <h4>Calories</h4>
          <div className="stat-item">
            <span>Daily Total: {stats.calories.daily}</span>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${Math.min(stats.calories.daily / caloriesDenoms.daily * 100, 100)}%` }} />
            </div>
          </div>
          <div className="stat-item">
            <span>Weekly Total: {stats.calories.weekly}</span>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${Math.min(stats.calories.weekly / caloriesDenoms.weekly * 100, 100)}%` }} />
            </div>
          </div>
          <div className="stat-item">
            <span>Monthly Total: {stats.calories.monthly}</span>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${Math.min(stats.calories.monthly / caloriesDenoms.monthly * 100, 100)}%` }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StatsWidget;
